import React from 'react'
import ReactDOM from 'react-dom';

import cookies from '../../services/cookies';
import {
    takeEvery,
    takeLeading,
    call,
    put,
    select,
} from 'redux-saga/effects';

import * as actionTypes from '../actions/actionTypes'
import * as actions from '../actions/index';
import history from '../../services/history';

import i18n from '../../translations/i18n';

//Components
import Notification from '../../components/Notifications/Notifications';

//Utils
import qs from 'qs';
import axios from '../../services/axios-client'
import camelize from 'camelize';
import snakeCase from 'snakecase-keys';
import moment from 'moment';

import * as AUTH_TYPES from '../actions/auth/actionTypes';

import * as AUTH_ACTIONS from '../actions/auth';

import * as AUTH_SELECTORS from '../selectors/auth';

import { HTTP_METHOD } from '../../const/http/HTTP_METHOD';
import { HTTP_STATUS_CODE } from '../../const/http/HTTP_STATUS_CODE';
import { RESET_PASSWORD_STEP } from '../../const/auth/RESET_PASSWORD_STEP';
import {
    API_CLIENT_PASSWORD_REQUEST_RESET_CODE_ROUTE,
    API_CLIENT_PASSWORD_SET_NEW_ROUTE,
    API_CLIENT_PASSWORD_VERIFY_RESET_CODE_ROUTE,
} from '../../const/http/API_URL';

export function* checkEmail({ email }) {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/register/check-email',
            data: qs.stringify({
                email
            }),
            headers: {
                'company-api-key': companyAPIKey
            }
        });

        const { next_step: step, hash, timer, already_registered: alreadyRegistered } = resp.data.result;

        yield put(actions.checkEmailSucceeded(step, hash, moment().add(timer, 'seconds').valueOf(), alreadyRegistered));
    } catch (err) {
        yield put(actions.checkEmailFailed());
    }
}

export function* verifyEmail({ code }) {
    try {
        const hash = yield select(state => state.auth.hash);
        const locale = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/register/verify-email',
            data: qs.stringify({
                hash,
                code
            }),
            headers: {
                locale
            }
        });

        const { next_step: step, hash: newHash } = resp.data.result;
        yield put(actions.verifyEmailSucceeded(step, newHash));
    } catch (err) {
        if (err.response && err.response.data && err.response.data.result && err.response.data.result.message) {
            ReactDOM.render(
                <Notification type='error' error={err} />,
                document.getElementById('notification')
            );
        }

        yield put(actions.verifyEmailFailed());
    }
}

export function* fillProfile(action) {
    const { payload } = action;
    const {
        firstName,
        lastName,
        password,
        contactNumber,
        birthDate,
    } = payload;

    try {
        const hash = yield select(state => state.auth.hash);
        const strictPath = yield select(state => state.widget.strictPath);
        const countryCode = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/register/fill-profile',
            data: qs.stringify(snakeCase({
                hash,
                password,
                contactNumber,
                firstName,
                lastName,
                countryCode,
                birthDate: moment(birthDate).format('Y-M-D')
            }))
        });

        const userToken = resp.data.result.access_token;
        cookies.set('access_token', userToken, {
            path: strictPath ? window.location.pathname : '/',
            domain: window.location.hostname,
            secure: true,
            sameSite: true,
        });
        yield put(actions.fillProfileSucceeded(userToken));
    } catch (err) {
        if (err.response && err.response.data && err.response.data.result && err.response.data.result.errors) {
            yield put(actions.fillProfileFailed(err.response.data.result.errors));
            return;
        }

        yield put(actions.fillProfileFailed(null));
    }
}

export function* verifyPassword({ password }) {
    try {
        const hash = yield select(state => state.auth.hash);
        const locale = yield select(state => state.locales.locale);
        const strictPath = yield select(state => state.widget.strictPath);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/register/verify-password',
            data: qs.stringify({
                hash,
                password
            }),
            headers: {
                locale
            }
        });

        const userToken = resp.data.result.access_token;
        cookies.set('access_token', userToken, {
            path: strictPath ? window.location.pathname : '/',
            domain: window.location.hostname,
            secure: true,
            sameSite: true,
        });
        yield put(actions.verifyPasswordSucceeded(userToken));
        yield put(actions.authenticate());
    } catch (err) {
        if (err.response && err.response.data && err.response.data.result && err.response.data.result.message) {
            ReactDOM.render(
                <Notification type='error' error={err} />,
                document.getElementById('notification')
            );
        }

        yield put(actions.verifyPasswordFailed());
    }
}

export function* requestPasswordReset() {
    try {
        const email = yield select(state => state.auth.checkedEmail);
        const redirectURL = 'widget';

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/password/reset-request',
            data: qs.stringify({
                email,
                redirect_url: redirectURL
            })
        });

        const timer = resp.data.result.timer;

        ReactDOM.render(
            <Notification type={!!timer ? 'error' : 'success'} message={i18n.t(`common:resetPasswordScreen.checkInbox`)} error={i18n.t(`common:resetPasswordScreen.pleaseWaitForEmail`)} />,
            document.getElementById('notification')
        );

        yield put(actions.requestPasswordResetSucceeded());
    } catch (error) {
        const { errors } = error.response.data.result;
        if (errors) {
            yield put(actions.requestPasswordResetFailed(camelize(errors)));
            return;
        }

        yield put(actions.requestPasswordResetFailed({ email: error.response.data.result.message }));
    }
}

export function* resetPassword({ token, newPassword }) {
    try {
        yield call(axios.request, {
            method: 'POST',
            url: '/api/client/password/reset',
            data: qs.stringify({
                confirmation_token: token,
                new_password: newPassword
            })
        });

        yield put(actions.resetPasswordSucceeded());
    } catch (error) {
        const { errors } = error.response.data.result;
        if (errors) {
            yield put(actions.resetPasswordFailed(camelize(errors)));
            return
        }

        yield put(actions.resetPasswordFailed(null, error.response.data.result.message));
    }
}

export function* restoreSession() {
    const userToken = cookies.get('access_token');
    if (!userToken) {
        return
    }

    yield put(actions.restoreSessionSucceeded(userToken));
}


export function* logout() {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        yield axios.request({
            method: 'POST',
            url: '/api/client/logout',
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            },
        });

        yield put(actions.logoutSucceeded());

        const strictPath = yield select(state => state.widget.strictPath);
        cookies.remove('access_token', {
            path: strictPath ? window.location.pathname : '/',
            domain: window.location.hostname
        });
        history.push('/signin/profile');

        ReactDOM.render(
            <Notification type='success' message={i18n.t('common:logout.logoutSucceeded')} />,
            document.getElementById('notification')
        );

    } catch (err) {
        if (err.response && err.response.status && Math.floor(err.response.status / 100) === 4) {
            yield put(actions.logoutSucceeded());

            const strictPath = yield select(state => state.widget.strictPath);
            cookies.remove('access_token', {
                path: strictPath ? window.location.pathname : '/',
                domain: window.location.hostname
            });

            history.push('/signin/profile');

            ReactDOM.render(
                <Notification type='error' error={i18n.t('common:logout.sessionTimedOut')} />,
                document.getElementById('notification')
            );
            return;
        }

        ReactDOM.render(
            <Notification type='error' error={err} />,
            document.getElementById('notification')
        );
    }
}

function* requestResetPasswordCode() {
    const { checkedEmail } = yield select(AUTH_SELECTORS.authSelector);

    try {
        const res = yield call(axios.request, {
            method: HTTP_METHOD.POST,
            url: API_CLIENT_PASSWORD_REQUEST_RESET_CODE_ROUTE,
            data: qs.stringify({ email: checkedEmail }),
        });

        const { data } = res;
        const { code, result } = data;
        if (code === HTTP_STATUS_CODE.OK) {
            const { timer } = result;
            yield put(AUTH_ACTIONS.requestResetPasswordCodeSuccess({
                step: RESET_PASSWORD_STEP.VERIFY_CODE,
                timer: moment().add(timer, 'seconds').unix() * 1000,
            }));
        }
    } catch (err) {
        yield put(AUTH_ACTIONS.requestResetPasswordCodeFail());
        ReactDOM.render(
            <Notification type='error' error={err} />,
            document.getElementById('notification'),
        );
    }
}

function* verifyResetPasswordCode(action) {
    const { payload } = action;
    const { code: verificationCode } = payload;

    try {
        const res = yield call(axios.request, {
            method: HTTP_METHOD.POST,
            url: API_CLIENT_PASSWORD_VERIFY_RESET_CODE_ROUTE,
            data: qs.stringify({ code: verificationCode }),
        });

        const { data } = res;
        const { code } = data;
        if (code === HTTP_STATUS_CODE.OK) {
            yield put(AUTH_ACTIONS.verifyResetPasswordCodeSuccess({
                step: RESET_PASSWORD_STEP.SET_PASSWORD,
            }));
        }
    } catch (err) {
        yield put(AUTH_ACTIONS.verifyResetPasswordCodeFail());
        ReactDOM.render(
            <Notification type='error' error={err} />,
            document.getElementById('notification'),
        );
    }
}

function* setResetPassword(action) {
    const { payload } = action;
    const { code: verificationCode, password } = payload;

    const strictPath = yield select(state => state.widget.strictPath);

    try {
        const res = yield call(axios.request, {
            method: HTTP_METHOD.POST,
            url: API_CLIENT_PASSWORD_SET_NEW_ROUTE,
            data: qs.stringify({
                code: verificationCode,
                password,
            }),
        });

        const { data } = res;
        const { code, result } = data;
        if (code === HTTP_STATUS_CODE.OK) {
            const { access_token: accessToken } = result;
            cookies.set(
                'access_token',
                accessToken,
                {
                    path: strictPath ? window.location.pathname : '/',
                    domain: window.location.hostname,
                    secure: true,
                    sameSite: true,
                },
            );
            yield put(AUTH_ACTIONS.setResetPasswordSuccess({ accessToken }));
            yield put(actions.authenticate());
        }
    } catch (err) {
        yield put(AUTH_ACTIONS.setResetPasswordFail());
        ReactDOM.render(
            <Notification type='error' error={err} />,
            document.getElementById('notification'),
        );
    }
}

function* verifyPasswordSucceeded() {
    const stateLocale = yield select(state => state.locales.locale);
    const locale = cookies.get('locale') ?? stateLocale;

    yield put(actions.changeLocale(locale))
}

export const watchAuth = [
    takeLeading(actionTypes.CHECK_EMAIL, checkEmail),
    takeLeading(actionTypes.VERIFY_EMAIL, verifyEmail),
    takeLeading(actionTypes.FILL_PROFILE, fillProfile),
    takeLeading(actionTypes.VERIFY_PASSWORD, verifyPassword),
    takeLeading(actionTypes.REQUEST_PASSWORD_RESET, requestPasswordReset),
    takeEvery(actionTypes.RESET_PASSWORD, resetPassword),
    takeEvery(actionTypes.RESTORE_SESSION, restoreSession),
    takeLeading(actionTypes.LOGOUT, logout),
    takeLeading(AUTH_TYPES.REQUEST_RESET_PASSWORD_CODE, requestResetPasswordCode),
    takeLeading(AUTH_TYPES.VERIFY_RESET_PASSWORD_CODE, verifyResetPasswordCode),
    takeLeading(AUTH_TYPES.SET_RESET_PASSWORD, setResetPassword),
    takeLeading(AUTH_TYPES.VERIFY_PASSWORD_SUCCEEDED, verifyPasswordSucceeded),
];
